import { useContext } from 'react';
import { useQuery } from 'react-query';
import { useParams } from 'react-router-dom';
import { SettingsContext } from '../store/SettingsContext';
import { UserContext } from '../store/UserContext';
import useAxios from './useAxios';

export interface OnlyPromoteStatsBucket {
    /** ISO date `YYYY-MM-DD` (UTC). */
    date: string;
    count: number;
}

export interface OnlyPromoteStatsResponse {
    /** Inclusive UTC start of the window (ISO timestamp). */
    from: string;
    /** Exclusive UTC end of the window (ISO timestamp). */
    to: string;
    days: number;
    linkClicks: OnlyPromoteStatsBucket[];
    referrerClicks: OnlyPromoteStatsBucket[];
    menuCopies: OnlyPromoteStatsBucket[];
    liveStreamClicks: OnlyPromoteStatsBucket[];
}

export interface UseOnlyPromoteStatsOptions {
    days?: number;
    linkId?: number;
    referrerId?: number;
    menuItemId?: number;
    streamId?: number;
    enabled?: boolean;
}

const QUERY_KEY = 'onlyPromoteStats';

const useOnlyPromoteStats = (options: UseOnlyPromoteStatsOptions = {}) => {
    const settingsContext = useContext(SettingsContext);
    const userContext = useContext(UserContext);
    const params = useParams();
    const axios = useAxios();

    const { days = 30, linkId, referrerId, menuItemId, streamId, enabled = true } = options;

    const url = `${settingsContext.routes.menu.base}${params.userId}/stats`;

    const fetchStats = async (): Promise<OnlyPromoteStatsResponse> => {
        const response = await axios.get(url, {
            params: {
                days,
                ...(linkId ? { linkId } : {}),
                ...(referrerId ? { referrerId } : {}),
                ...(menuItemId ? { menuItemId } : {}),
                ...(streamId ? { streamId } : {}),
            },
        });
        return response.data as OnlyPromoteStatsResponse;
    };

    return useQuery(
        [QUERY_KEY, params.userId, days, linkId ?? null, referrerId ?? null, menuItemId ?? null, streamId ?? null],
        fetchStats,
        {
            staleTime: 60 * 1000,
            refetchInterval: 2 * 60 * 1000,
            refetchOnWindowFocus: false,
            enabled: enabled && !!(userContext.jwtToken && params.userId && settingsContext.apiKey),
        },
    );
};

export default useOnlyPromoteStats;
